import type { Comment, Persisted } from '@tunasong/schemas'
import { getExchange, isComment } from '@tunasong/schemas'
import { groupExchanges, type AnchoredExchange } from '../exchanges.js'
import { isProposal, type ProposalComment } from '../proposal-resolve.js'

/** One item in the Agents thread: an exchange group, a plain chat comment, or a proposal outside any exchange */
export type ThreadItem =
  | { kind: 'exchange'; exchange: AnchoredExchange; at: string }
  | { kind: 'comment'; comment: Persisted<Comment>; at: string }
  | { kind: 'proposal'; proposal: ProposalComment; at: string }

/**
 * The thread, oldest first (newest at the bottom, by the composer). An exchange is one group, placed where its
 * first comment was (Option A §6; chat makeover "the exchange is the unit").
 */
export const buildThread = (agentId: string, comments: Persisted<Comment>[]): ThreadItem[] => {
  const exchanges = groupExchanges(agentId, comments)
  const firstAt = (exchange: AnchoredExchange) =>
    [exchange.question, ...exchange.replies, ...exchange.proposals]
      .flatMap(member => (member ? [member.createdAt] : []))
      .sort()[0] ?? exchange.lastAt
  const items: ThreadItem[] = exchanges.map(exchange => ({ kind: 'exchange', exchange, at: firstAt(exchange) }))
  for (const comment of comments) {
    if (getExchange(comment)) {
      continue
    }
    items.push(
      isProposal(comment)
        ? { kind: 'proposal', proposal: comment, at: comment.createdAt }
        : { kind: 'comment', comment, at: comment.createdAt }
    )
  }
  return items.sort((a, b) => a.at.localeCompare(b.at))
}

/**
 * The agent's comments from both delivery paths: the fetched children and the WebSocket pushes (a lost push still
 * arrives through the fetch on remount). One per id; the newest update wins.
 */
export const mergeComments = (agentId: string, fetched: unknown[], pushed: unknown[]): Persisted<Comment>[] => {
  const byId = new Map<string, Persisted<Comment>>()
  for (const entity of [...fetched, ...pushed]) {
    if (!isComment(entity)) {
      continue
    }
    const comment = entity as Persisted<Comment>
    if (comment.parentId !== agentId || !comment.id) {
      continue
    }
    const known = byId.get(comment.id)
    if (!known || (comment.updatedAt ?? '') >= (known.updatedAt ?? '')) {
      byId.set(comment.id, comment)
    }
  }
  return [...byId.values()]
}
